<template>
  <v-container fluid class="pa-4">
    <div class="d-flex align-center mb-3">
      <v-icon class="mr-2">mdi-shield-account-outline</v-icon>
      <h2 class="text-h5 mb-0">Роли и права</h2>
      <v-spacer />
      <v-btn color="primary" size="small" prepend-icon="mdi-plus" @click="openCreateRole">Новая роль</v-btn>
    </div>
    <p class="text-body-2 text-medium-emphasis mb-4">
      Право — ключ доступа к функции (например, <code>feature:onion_sources</code>). Роль — набор прав, назначается пользователям
      в «Управлении пользователями». Админ имеет все права всегда.
    </p>

    <v-card>
      <v-table density="comfortable">
        <thead>
          <tr>
            <th style="min-width: 220px;">Право</th>
            <th v-for="r in roles" :key="r.id" class="text-center">
              <div class="font-weight-medium">{{ r.name }}</div>
              <div class="text-caption text-grey">{{ r.description || '' }}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in permissions" :key="p.id">
            <td>
              <div class="font-weight-medium">{{ p.name }}</div>
              <div class="text-caption text-grey">{{ p.description || (p.resource + ' · ' + p.action) }}</div>
            </td>
            <td v-for="r in roles" :key="r.id" class="text-center">
              <v-checkbox-btn
                v-if="r.name !== 'admin'"
                :model-value="has(r.id, p.id)"
                :disabled="busy"
                color="primary"
                class="d-inline-flex"
                @update:model-value="toggle(r, p, $event)"
              />
              <v-icon v-else color="grey" title="Админ имеет все права">mdi-check-all</v-icon>
            </td>
          </tr>
          <tr v-if="!permissions.length">
            <td :colspan="roles.length + 1" class="text-center text-caption text-grey pa-6">Права ещё не заведены</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <v-dialog v-model="createDialog" max-width="480">
      <v-card>
        <v-card-title>Новая роль</v-card-title>
        <v-card-text>
          <v-text-field v-model="newRole.name" label="Имя роли (латиницей, напр. onion-beta)" variant="outlined" density="comfortable" />
          <v-text-field v-model="newRole.description" label="Описание" variant="outlined" density="comfortable" />
          <div v-if="createError" class="text-error text-caption">{{ createError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="createDialog = false">Отмена</v-btn>
          <v-btn color="primary" :loading="busy" :disabled="!newRole.name" @click="createRole">Создать</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">{{ snackbar.text }}</v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient';

export default {
  name: 'RolesPermissions',
  data() {
    return {
      roles: [],
      permissions: [],
      rolePerms: {},       // role_id → Set(permission_id)
      busy: false,
      createDialog: false,
      newRole: { name: '', description: '' },
      createError: '',
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  mounted() { this.load(); },
  methods: {
    notify(text, color = 'success') { this.snackbar = { show: true, text, color }; },
    has(roleId, permId) { return !!(this.rolePerms[roleId] && this.rolePerms[roleId].has(permId)); },
    async load() {
      this.busy = true;
      try {
        const [rolesRes, permsRes] = await Promise.all([apiClient.get('/admin/roles'), apiClient.get('/admin/permissions')]);
        this.roles = rolesRes.data.roles || rolesRes.data.items || rolesRes.data || [];
        this.permissions = permsRes.data.permissions || permsRes.data.items || permsRes.data || [];
        const map = {};
        await Promise.all(this.roles.map(async r => {
          try {
            const { data } = await apiClient.get(`/admin/roles/${r.id}/permissions`);
            map[r.id] = new Set((data.permissions || []).map(p => p.id));
          } catch (e) { map[r.id] = new Set(); }
        }));
        this.rolePerms = map;
      } catch (e) {
        this.notify('Не удалось загрузить роли и права', 'error');
      } finally { this.busy = false; }
    },
    async toggle(role, perm, value) {
      this.busy = true;
      try {
        if (value) {
          await apiClient.post(`/admin/roles/${role.id}/permissions`, { role_id: role.id, permission_id: perm.id });
          this.rolePerms[role.id].add(perm.id);
        } else {
          await apiClient.delete(`/admin/roles/${role.id}/permissions/${perm.id}`);
          this.rolePerms[role.id].delete(perm.id);
        }
        this.rolePerms = { ...this.rolePerms };
        this.notify(`${perm.name}: ${value ? 'выдано' : 'снято'} для роли ${role.name}. Пользователи увидят изменение после перезагрузки страницы.`);
      } catch (e) {
        this.notify(e?.response?.data?.detail || 'Ошибка изменения права', 'error');
      } finally { this.busy = false; }
    },
    openCreateRole() { this.newRole = { name: '', description: '' }; this.createError = ''; this.createDialog = true; },
    async createRole() {
      this.busy = true; this.createError = '';
      try {
        await apiClient.post('/admin/roles', { name: this.newRole.name.trim(), description: this.newRole.description || null });
        this.createDialog = false;
        await this.load();
        this.notify(`Роль ${this.newRole.name} создана`);
      } catch (e) {
        this.createError = e?.response?.data?.detail || 'Не удалось создать роль';
      } finally { this.busy = false; }
    },
  },
};
</script>
